import { z } from "zod";
import { AMAZON_ADV_API_CLIENT_ID } from "../../../utils/gvSecrets";
import { isLocalhost } from "../../../utils/getUrlPrefix";

const LOCAL_APP_URL = "https://localhost:3000";
const PRODUCTION_APP_URL = "https://app.gotrellis.com";

function getAppUrl(): string {
  return isLocalhost ? LOCAL_APP_URL : PRODUCTION_APP_URL;
}

interface AmazonAdvOAuthConfig {
  successPath?: string;
  errorPath?: string;
  reauthorize?: boolean;
  additional_ads_credentials?: boolean;
}

const encodedStateSchema = z.object({
  successPath: z.string(),
  errorPath: z.string(),
  reauthorize: z.boolean().optional(),
  additional_ads_credentials: z.boolean().optional(),
});

type EncodedState = z.infer<typeof encodedStateSchema>;

export function initAmazonAdvertisingOAuth(config?: AmazonAdvOAuthConfig): void {
  const defaultSuccessPath = "/onboarding/v2/profiles";
  const defaultErrorPath = window.location.pathname + window.location.search;

  const successPath = config?.successPath ?? defaultSuccessPath;
  const errorPath = config?.errorPath ?? defaultErrorPath;

  const statePayload: EncodedState = {
    successPath,
    errorPath,
    reauthorize: config?.reauthorize,
    additional_ads_credentials: config?.additional_ads_credentials,
  };

  const encodedState = btoa(JSON.stringify(statePayload));
  const redirectUri = `${getAppUrl()}/oauth/amazon/advertising`;

  const oauthUrl = new URL("https://www.amazon.com/ap/oa");
  oauthUrl.searchParams.set("client_id", AMAZON_ADV_API_CLIENT_ID);
  oauthUrl.searchParams.set("scope", "profile cpc_advertising:campaign_management");
  oauthUrl.searchParams.set("response_type", "code");
  oauthUrl.searchParams.set("redirect_uri", redirectUri);
  oauthUrl.searchParams.set("state", encodedState);

  window.location.href = oauthUrl.toString();
}

export function initAmazonAdvertisingReauth(): void {
  initAmazonAdvertisingOAuth({
    successPath: "/user/amzsummary?notification=reauthSuccess",
    reauthorize: true,
  });
}

export function initAmazonAdvertisingAdditionalAccount(): void {
  initAmazonAdvertisingOAuth({
    successPath: "/user/amzsummary?modal=profileImport&notification=additionalAccountSuccessfullyAdded",
    additional_ads_credentials: true,
  });
}

export function decodeOAuthState(encodedState: string | null): EncodedState | null {
  if (!encodedState) return null;

  try {
    const decoded = JSON.parse(atob(encodedState));
    const result = encodedStateSchema.safeParse(decoded);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
